/**
 * Rival lab cinematic — MVP PR 15 (H7b) + PR A4 post-battle exit.
 *
 * Cartridge: after the player picks a starter and walks toward the lab
 * door, Blue intercepts on row 8 and challenges them to a battle. The
 * runtime.json already has 3 coord-event triggers at (5/6/7, 8) gated
 * on VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB == 3; PR A3's
 * labPostPick handler advances state to 3 at its end, so the next step
 * onto row 8 fires this handler.
 *
 * Two halves split across battle:
 *
 *   PRE-BATTLE (rivalLabHandler):
 *     ┌─────────────────────────────────────────────────────────────┐
 *     │  [bracket entry: follower recalled to its ball — round 6;    │
 *     │   no restore at exit, the battle WAKE re-derive brings it    │
 *     │   back]                                                      │
 *     │  player pivots up                                            │
 *     │  "RIVAL: Wait, RED!"                                         │
 *     │  setFlag(HIDE_RIVAL_IN_LAB) + refresh                        │
 *     │  spawn walker @ rivalChallengeStart (home (5,4), or the      │
 *     │    flee re-arm tile) → rivalApproachWaypoints to             │
 *     │    (player.x, player.y - 1)                                  │
 *     │  "Let's check out our POKéMON! Come on, I'll take you on!"   │
 *     │  setVar(VAR_RIVAL_LAST_TILE, "x,y") ◄── post-battle re-spawn │
 *     │  setFlag(RIVAL_DEFEATED_PALLET)     ◄── pre-emptive; loss    │
 *     │                                         resets it via        │
 *     │                                         blackout flow        │
 *     │  walker.destroy()                                            │
 *     │  launchTrainerBattleByRef(battleRef)                         │
 *     └─────────────────────────────────────────────────────────────┘
 *
 *   POST-BATTLE (rivalPostBattleHandler — dispatched from WAKE gate
 *   when RIVAL_BATTLE_RESOLVED is set; gate cleared at handler-end):
 *     ┌─────────────────────────────────────────────────────────────┐
 *     │  in-memory postBattleCinematicActive guard (session only)    │
 *     │  parse VAR_RIVAL_LAST_TILE → (x, y)                          │
 *     │  spawn walker @ (x, y) facing down                            │
 *     │  "WHAT? Unbelievable! I picked the wrong POKéMON!"            │
 *     │  "Okay! I'll make my POKéMON battle to toughen it up!"       │
 *     │  "RED! Gramps! Smell you later!"                              │
 *     │  exit walk via rivalExitWaypoints — a column the player      │
 *     │    is NOT standing in (third-playtest deadlock fix), down    │
 *     │    to row 11, along to the door (6, 12)                      │
 *     │  walker.destroy(); third ball vanishes (Oak collects)        │
 *     │  setFlag(RIVAL_POST_BATTLE_DONE)  ◄── COMPLETION latch —      │
 *     │  setLabSceneState(SEALED)             only after the walk    │
 *     │  clearFlag(RIVAL_BATTLE_RESOLVED)     finished; stalls retry │
 *     │  save()                               on next WAKE/reload    │
 *     └─────────────────────────────────────────────────────────────┘
 *
 * Cartridge text verbatim from
 * pokefirered/data/maps/PalletTown_ProfessorOaksLab/text.inc
 * (Text_RivalLetsCheckOutMons + Text_RivalDefeat + Text_RivalGoToughenMyMon).
 */
import type { Handler, ScriptCtx } from '../scriptRunner';
import { effectsReduced } from '../../render/motion';
import { rivalBattleRefForStarter } from '../../data/trainerParties';
import { VAR_RIVAL_BATTLE_OUTCOME } from '../../battle/battleRules';
import {
  setLabSceneState,
  getLabSceneState,
  LAB_SCENE_STATES,
} from '../labScene';

const RIVAL_WAIT_LINE = ['RIVAL: Wait, RED!'];
const RIVAL_CHALLENGE_LINES = [
  "Let's check out our POKéMON!",
  "Come on, I'll take you on!",
];

// PR A4: cartridge post-battle dialogue (Text_RivalDefeat +
// Text_RivalGoToughenMyMon).
const RIVAL_DEFEATED = [
  'WHAT?',
  'Unbelievable!',
  'I picked the wrong POKéMON!',
];
const RIVAL_SMELL_YOU_LATER = [
  "RIVAL: Okay! I'll make my POKéMON",
  'battle to toughen it up!',
  'RED! Gramps!',
  'Smell you later!',
];
// Round-4 flee-as-concession: the LOSS variant (wipe or flee — both
// resolve here). Gloat register, terse per dialogue.ts convention
// (cartridge Text_RivalWin + the toughen-up exit line).
const RIVAL_GLOAT = ['RIVAL: Yeah! Am I great or what!'];
const RIVAL_GO_TRAIN = ['Go toughen up your POKéMON!', 'Smell you later!'];

const RIVAL_SPAWN = { x: 5, y: 4 } as const;
// Lab door tile. Walker walks here at the end of the post-battle
// cinematic, then destroys — visual analog of cartridge `set_invisible`
// after stepping onto the warp tile.
const LAB_DOOR = { x: 6, y: 12 } as const;
const VAR_RIVAL_LAST_TILE = 'VAR_RIVAL_LAST_TILE';

/** Parse a "x,y" tile var. Shared by the flee re-arm predicate and the
 *  challenge-start resolver so both read the var identically. */
function parseTileVar(
  raw: string | undefined,
): { x: number; y: number } | null {
  if (!raw) return null;
  const parts = raw.split(',').map((s) => Number(s));
  if (!Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) return null;
  return { x: parts[0]!, y: parts[1]! };
}

/** Where the challenge walker starts. First challenge: the rival's
 *  map-home beside Oak (5,4). Re-challenge after a flee: the battle-era
 *  tile recorded in VAR_RIVAL_LAST_TILE — the SAME tile the round-5
 *  re-arm moved the static NPC to, so the hide→walker swap is
 *  positionally seamless instead of teleporting him back to Oak's
 *  side. Exported for the path matrix. */
export function rivalChallengeStart(
  lastTileRaw: string | undefined,
): { x: number; y: number } {
  return parseTileVar(lastTileRaw) ?? { ...RIVAL_SPAWN };
}

/** Pre-battle approach waypoints: X-align on the start row to the
 *  player's column, then south to the challenge tile one north of the
 *  player. Exported so the test matrix pins the SAME function the
 *  handler runs (round-6 — this was the one unpinned leg, and it was
 *  the one that walked over the follower).
 *
 *  NOTE the destination (px, py-1) IS the follower's trailing tile
 *  whenever the player walked south into the trigger — avoidance is
 *  geometrically impossible for this leg, which is why the handler
 *  recalls the follower (HGSS convention) BEFORE walking. The matrix
 *  pins player-tile avoidance + collision-validity; the recall-before-
 *  walk ordering is pinned by the choreography test. */
export function rivalApproachWaypoints(
  rivalStart: { x: number; y: number },
  playerTile: { x: number; y: number },
): Array<{ x: number; y: number }> {
  return [
    { x: playerTile.x, y: rivalStart.y }, // X-align on the start row
    { x: playerTile.x, y: playerTile.y - 1 }, // south to the challenge tile
  ];
}

export const rivalLabHandler: Handler = async (ctx: ScriptCtx) => {
  // Self-gate: skip if rival already defeated this run, or if the
  // starter pick hasn't happened (defensive — the trigger is var-gated
  // by DraftScene.commit's setVar, so this shouldn't reach here pre-
  // pick, but the belt-and-suspenders flag check keeps the handler
  // idempotent against any future trigger-fire surprises).
  if (ctx.runState.hasFlag(ctx.FLAGS.RIVAL_DEFEATED_PALLET)) {
    // Flee-hole repair (round-4). The flag above is set FUTURE-TENSE
    // before the battle; a flee leaves it set with the battle
    // unresolved and the lab parked at POST_PICK. runFromBattle's
    // fledTrainerFlagResets is the PRIMARY restore (clears this flag +
    // HIDE_RIVAL_IN_LAB at flee time so the encounter stays OPEN and
    // re-triggerable) — this branch is the fallback for saves written
    // before that fix, and defense-in-depth against any future exit
    // path that strands the same fingerprint: state 3 with neither
    // RIVAL_BATTLE_RESOLVED nor RIVAL_POST_BATTLE_DONE. A won-or-
    // wiped-but-goodbye-pending wake has RIVAL_BATTLE_RESOLVED set
    // and never matches; SEALED (state 4) never matches.
    const fleeParked =
      getLabSceneState() === LAB_SCENE_STATES.POST_PICK &&
      !ctx.runState.hasFlag(ctx.FLAGS.RIVAL_BATTLE_RESOLVED) &&
      !ctx.runState.hasFlag(ctx.FLAGS.RIVAL_POST_BATTLE_DONE);
    if (!fleeParked) return;
    ctx.runState.clearFlag(ctx.FLAGS.RIVAL_DEFEATED_PALLET);
    // The static NPC may still be hidden if the flee predates the
    // runFromBattle restore — un-hide so the walker beat's hide→spawn
    // dance starts from the expected visual state.
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_RIVAL_IN_LAB);
  }
  if (!ctx.runState.hasFlag(ctx.FLAGS.STARTER_PICKED)) return;

  const playerStarter = ctx.runState.getParty()[0]?.species;
  if (!playerStarter) return;
  const battleRef = rivalBattleRefForStarter(playerStarter);
  if (!battleRef) return;

  // recallFollower (round-6): the challenge destination (px, py-1) IS
  // the follower's trailing tile when the player walked south into the
  // trigger — the field report "rival walked on top of my starter."
  // Avoidance is geometrically impossible for this leg; the cartridge
  // answer is that your Pokémon is never on the lawn during a trainer
  // challenge — it enters the battle from its ball. restoreFollower:
  // false because this bracket ENDS by launching the battle: the
  // recall already matches the battle SLEEP's follower state, and the
  // post-battle WAKE re-derive brings it back on every outcome.
  await ctx.worldScene.runHandlerBracket(async () => {
    const walkSpeed = effectsReduced() ? 192 : 96;
    const player = ctx.worldScene.getPlayerTile();

    // Beat 1: pivot player up (was facing south, walking toward the
    // door) and deliver the "Wait!" line. Pause player input via the
    // bracket's transitioning lock.
    ctx.worldScene.setPlayerFacing('up');
    ctx.worldScene.showDialogue(RIVAL_WAIT_LINE, null);
    await ctx.worldScene.waitForDialogueClose();

    // Beat 2: hide the static rival NPC and spawn a handler-owned
    // walker where he actually stands — his map-home (5,4) on the
    // first challenge, or the round-5 re-arm tile after a flee (the
    // static NPC was repositioned there; spawning at home would
    // visibly teleport him). Approach: X-align on the start row, then
    // south to one tile north of the player (cartridge keeps the
    // rival a tile north for the dialogue + battle launch).
    ctx.runState.setFlag(ctx.FLAGS.HIDE_RIVAL_IN_LAB);
    ctx.worldScene.refreshNpcVisibility();
    const start = rivalChallengeStart(
      ctx.runState.getVar(VAR_RIVAL_LAST_TILE),
    );
    const rivalHandle = await ctx.worldScene.npcWalkAlongPath({
      spriteKey: 'blue',
      startTile: start,
      waypoints: rivalApproachWaypoints(start, player),
      pixelsPerSecond: walkSpeed,
    });

    // Beat 3: full challenge dialogue.
    ctx.worldScene.showDialogue(RIVAL_CHALLENGE_LINES, null);
    await ctx.worldScene.waitForDialogueClose();

    // Pre-battle flag mutations. Setting RIVAL_DEFEATED_PALLET here is
    // semantically "future-tense" — battle hasn't started yet — but it
    // collapses the rivalLab self-gate (line 52). On loss the blackout
    // cinematic forces a Reset Run which wipes the flag, so the gate
    // stays correct: win→stays / loss-reset→wiped.
    ctx.runState.setFlag(ctx.FLAGS.RIVAL_DEFEATED_PALLET);

    // PR A4: store the rival's last on-screen tile so the post-battle
    // cinematic can re-spawn the walker at the same spot. Inline parse
    // per eng-review decision 4 — no helper module for one consumer.
    ctx.runState.setVar(
      VAR_RIVAL_LAST_TILE,
      `${player.x},${player.y - 1}`,
    );

    // NOTE (PR A4): we DO NOT advance the lab-scene var to 4 here.
    // Post-battle handler owns that transition at handler-end. State
    // stays at 3 (POST_PICK) until "Smell you later!" + exit complete.

    // Destroy the handler-owned rival sprite. BattleScene fades in over
    // the lab so the destroy is invisible; static rival NPC stays hidden
    // (HIDE_RIVAL_IN_LAB set above) once the player returns post-battle.
    rivalHandle.sprite.destroy();

    ctx.worldScene.save();
    ctx.worldScene.launchTrainerBattleByRef(battleRef);
  }, { recallFollower: true, restoreFollower: false });
};

/**
 * PR A4: rival post-battle exit cinematic.
 *
 * Dispatched from WorldScene's WAKE handler when:
 *
 *     mapData == OaksLab
 *     && hasFlag(RIVAL_BATTLE_RESOLVED)
 *     && !hasFlag(RIVAL_POST_BATTLE_DONE)
 *
 * Synthetic scriptRef `__rival_post_battle__`. The walker re-spawns at
 * VAR_RIVAL_LAST_TILE (set by pre-battle), monologues, then walks
 * south to the lab door (6, 12) and destroys. State advances to 4
 * (SEALED).
 *
 * Runs on BOTH resolved outcomes (round-4 sparring ruling): WIN sets
 * outcome 'win' → disbelief + "Smell you later!"; WIPE-LOSS is
 * survivable (starter healed, no career death) and sets outcome
 * 'loss' → the gloat variant. Only a FLEE leaves the encounter open —
 * no RIVAL_BATTLE_RESOLVED, this handler doesn't run, and the flee
 * resets re-arm the row-8 trigger instead.
 */
/** In-flight re-entrancy guard — session memory only. The PERSISTED
 *  RIVAL_POST_BATTLE_DONE latch now sets at the END of the walk, so a
 *  stalled or interrupted cinematic retries on the next WAKE/reload
 *  instead of sealing unfired. Third-playtest root cause: the old
 *  entry latch + a player-blocked exit path = rival frozen off-frame
 *  forever, gate permanently closed ("where did he go?"). */
let postBattleCinematicActive = false;

/** Post-battle exit waypoints. The player is standing on a row-8
 *  trigger tile (x ∈ {5,6,7}) — the OLD path X-aligned to col 6 and
 *  marched straight south THROUGH row 8, where npcWalkAlongPath's
 *  no-phase-through rule waits forever if the player is in the way.
 *  New geometry: pick an exit column the player is NOT standing in
 *  (rows 7-11 are fully walkable; row 8 is walkable only at 5/6/7),
 *  descend it to row 11, then approach the door (6, 12) along row 11.
 *  Exported for the path-vs-player regression test. */
export function rivalExitWaypoints(
  start: { x: number; y: number },
  playerX: number,
): Array<{ x: number; y: number }> {
  const exitCol = playerX === 5 ? 7 : 5;
  return [
    { x: exitCol, y: start.y },        // X-align on row 7, away from player col
    { x: exitCol, y: LAB_DOOR.y - 1 }, // south to row 11 (clears row 8 beside the player)
    { x: LAB_DOOR.x, y: LAB_DOOR.y - 1 }, // along row 11 to the door column
    { x: LAB_DOOR.x, y: LAB_DOOR.y },  // onto the door tile
  ];
}

export const rivalPostBattleHandler: Handler = async (ctx: ScriptCtx) => {
  if (postBattleCinematicActive) return;
  if (ctx.runState.hasFlag(ctx.FLAGS.RIVAL_POST_BATTLE_DONE)) return;

  postBattleCinematicActive = true;
  try {
  // recallFollower (round-5, HGSS convention): the goodbye walk's exit
  // columns can cross the follower's trailing tile; recalling into the
  // ball for the beat removes the collision class entirely, and the
  // send-out pop at bracket-end is a nice re-greeting after the rival
  // leaves. labPostPick deliberately does NOT do this — the starter
  // watches that one from the player's right.
  await ctx.worldScene.runHandlerBracket(async () => {
    const walkSpeed = effectsReduced() ? 192 : 96;

    // Battle-era tile from VAR_RIVAL_LAST_TILE via the shared resolver
    // (round-6: same parse the challenge start + flee re-arm use).
    // Falls back to (5,4) if missing/malformed — defensive, pre-battle
    // always sets it before destroying the walker.
    const { x: startX, y: startY } = rivalChallengeStart(
      ctx.runState.getVar(VAR_RIVAL_LAST_TILE),
    );

    // Spawn walker at the last tile, facing down (toward the door).
    // Static rival NPC is still HIDE_RIVAL_IN_LAB-flagged from
    // pre-battle, so no two-rivals overlap.
    const walker = await ctx.worldScene.npcWalkAlongPath({
      spriteKey: 'blue',
      startTile: { x: startX, y: startY },
      waypoints: [{ x: startX, y: startY }], // no-op; sprite mounts at start
      pixelsPerSecond: walkSpeed,
    });

    // Cartridge post-battle dialogue — outcome variant. 'win' (player
    // won) = disbelief + Smell you later; 'loss' (wipe OR flee — the
    // concession ruling converges them) = the gloat register. Missing
    // var defaults to 'win' defensively (pre-outcome-var saves).
    const outcome =
      ctx.runState.getVar(VAR_RIVAL_BATTLE_OUTCOME) === 'loss'
        ? 'loss'
        : 'win';
    if (outcome === 'loss') {
      ctx.worldScene.showDialogue(RIVAL_GLOAT, null);
      await ctx.worldScene.waitForDialogueClose();
      ctx.worldScene.showDialogue(RIVAL_GO_TRAIN, null);
      await ctx.worldScene.waitForDialogueClose();
    } else {
      ctx.worldScene.showDialogue(RIVAL_DEFEATED, null);
      await ctx.worldScene.waitForDialogueClose();
      ctx.worldScene.showDialogue(RIVAL_SMELL_YOU_LATER, null);
      await ctx.worldScene.waitForDialogueClose();
    }

    // Exit walk — player-avoiding geometry (see rivalExitWaypoints).
    const playerNow = ctx.worldScene.getPlayerTile();
    await ctx.worldScene.npcWalkAlongPath({
      existingSprite: walker.sprite,
      spriteKey: 'blue', // ignored when existingSprite is set
      startTile: { x: startX, y: startY },
      waypoints: rivalExitWaypoints({ x: startX, y: startY }, playerNow.x),
      pixelsPerSecond: walkSpeed,
    });
    walker.sprite.destroy();

    // The third, unclaimed ball leaves the table with the rival — Oak
    // collects it (cartridge: the lab's long-term state has an empty
    // table). No extra dialogue; the refresh happens under the door
    // beat where nobody's looking.
    const remaining = (['bulbasaur', 'charmander', 'squirtle'] as const).find(
      (s) =>
        s !== ctx.runState.getParty()[0]?.species &&
        !ctx.runState.hasFlag(
          ctx.FLAGS[
            s === 'bulbasaur'
              ? 'HIDE_BULBASAUR_BALL'
              : s === 'charmander'
                ? 'HIDE_CHARMANDER_BALL'
                : 'HIDE_SQUIRTLE_BALL'
          ],
        ),
    );
    if (remaining === 'bulbasaur') ctx.runState.setFlag(ctx.FLAGS.HIDE_BULBASAUR_BALL);
    else if (remaining === 'charmander') ctx.runState.setFlag(ctx.FLAGS.HIDE_CHARMANDER_BALL);
    else if (remaining === 'squirtle') ctx.runState.setFlag(ctx.FLAGS.HIDE_SQUIRTLE_BALL);
    ctx.worldScene.refreshNpcVisibility();

    // COMPLETION latch + CARTRIDGE STATE 4 — SEALED, written together
    // only after the walk actually finished. HIDE_RIVAL_IN_LAB stays
    // set so the static NPC never re-mounts.
    ctx.runState.setFlag(ctx.FLAGS.RIVAL_POST_BATTLE_DONE);
    setLabSceneState(LAB_SCENE_STATES.SEALED);

    // Clear the one-use post-battle signals — RIVAL_BATTLE_RESOLVED +
    // the outcome var were set by BattleScene's eventHandlers (victory
    // OR sparring wipe-loss); consumed here.
    ctx.runState.clearFlag(ctx.FLAGS.RIVAL_BATTLE_RESOLVED);
    ctx.runState.clearVar(VAR_RIVAL_BATTLE_OUTCOME);

    ctx.worldScene.save();
  }, { recallFollower: true });
  } finally {
    postBattleCinematicActive = false;
  }
};

/** Round-5 flee re-arm placement. After a rival flee, battleRules'
 *  flag resets make the STATIC rival NPC visible again — but the map-
 *  authored entry sits at his home tile (5,4) next to Oak, while the
 *  player left him mid-challenge at VAR_RIVAL_LAST_TILE. This pure
 *  predicate returns the battle-era tile exactly when the world is in
 *  the fled-rival fingerprint state (POST_PICK, unresolved, un-hidden,
 *  no post-battle latch, last-tile recorded); WorldScene mutates the
 *  npc event to it before rebuilding sprites. Map RELOAD deliberately
 *  reverts him to (5,4): that's his canon pre-battle posture beside
 *  Oak, and re-entering the lab forces a row-8 crossing that replays
 *  the challenge walk-in from there — coherent either way. Facing
 *  needs no mutation (authored MOVEMENT_TYPE_FACE_DOWN already faces
 *  the room). */
export function fledRivalRearmTile(deps: {
  labState: number;
  resolved: boolean;
  postBattleDone: boolean;
  hidden: boolean;
  lastTileRaw: string | undefined;
}): { x: number; y: number } | null {
  if (deps.labState !== LAB_SCENE_STATES.POST_PICK) return null;
  if (deps.resolved || deps.postBattleDone || deps.hidden) return null;
  return parseTileVar(deps.lastTileRaw);
}

export const RIVAL_LOCAL_ID = 'LOCALID_OAKS_LAB_RIVAL';
export { VAR_RIVAL_LAST_TILE };

/** Trigger refs registered for the rival cinematic. All three resolve
 *  to the same handler; the column the player walked through doesn't
 *  affect the cinematic. */
export const RIVAL_LAB_TRIGGER_REFS: readonly string[] = [
  'PalletTown_ProfessorOaksLab_EventScript_RivalBattleTriggerLeft',
  'PalletTown_ProfessorOaksLab_EventScript_RivalBattleTriggerMid',
  'PalletTown_ProfessorOaksLab_EventScript_RivalBattleTriggerRight',
];
